document.addEventListener("click", async (event) => {
    const btn = event.target.closest(".sample-box-random-fill");
    if (!btn) return;

    event.preventDefault();

    if (btn.disabled) return;
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");

    try {
        const parentLineKey = btn.dataset.sampleBoxParentKey;
        const maxSize = parseInt(btn.dataset.sampleBoxSize || "0", 10);
        const currentCount = parseInt(btn.dataset.sampleBoxCurrentCount || "0", 10);
        const collectionHandle = btn.dataset.sampleBoxCollectionHandle || "samples";

        const existingIdsRaw = (btn.dataset.sampleBoxExistingIds || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        const existingIds = existingIdsRaw.map((id) => String(id));

        // Get existing brands from data attribute
        const existingBrandsRaw = (btn.dataset.sampleBoxExistingBrands || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        const existingBrands = existingBrandsRaw.map((brand) => brand.toLowerCase());

        const slotsToFill = maxSize - currentCount;
        if (slotsToFill <= 0) {
            console.log("[SampleBox] No empty slots to fill");
            return;
        }

        // 1) Load candidates from the Samples collection
        const res = await fetch(
            `/collections/${collectionHandle}/products.json?limit=250`
        );
        if (!res.ok) {
            console.error("[SampleBox] Failed to load samples collection");
            return;
        }

        const data = await res.json();
        const candidateVariants = [];

        // Collect all available variants with their brand info
        (data.products || []).forEach((product) => {
            const brand = (product.vendor || "").toLowerCase();

            (product.variants || []).forEach((variant) => {
                if (!variant.available) return;
                const vid = String(variant.id);
                if (!existingIds.includes(vid)) {
                    candidateVariants.push({
                        id: vid,
                        brand: brand,
                    });
                }
            });
        });

        if (candidateVariants.length === 0) {
            console.log("[SampleBox] No available sample variants to add");
            return;
        }

        // 2) Pick random unique variants up to slotsToFill, ensuring no brand duplicates
        const usedBrands = new Set(existingBrands);
        const chosenVariantIds = [];
        const pool = [...candidateVariants];

        for (let i = 0; i < slotsToFill && pool.length > 0; i++) {
            // Filter pool to exclude brands already used
            const availablePool = pool.filter(v => !usedBrands.has(v.brand));

            if (availablePool.length === 0) {
                console.log("[SampleBox] No more unique brands available");
                break;
            }

            // Pick random variant from available pool
            const idx = Math.floor(Math.random() * availablePool.length);
            const chosen = availablePool[idx];

            chosenVariantIds.push(chosen.id);
            usedBrands.add(chosen.brand);

            // Remove the chosen variant from the main pool
            const poolIdx = pool.findIndex(v => v.id === chosen.id);
            if (poolIdx > -1) {
                pool.splice(poolIdx, 1);
            }
        }

        if (chosenVariantIds.length === 0) {
            console.log("[SampleBox] Nothing to add");
            return;
        }

        // 3) Build items payload to add as nested children under parent_line_key
        // Mirror the parent box's _box_number (set on both 5x and 10x boxes) onto its samples.
        const cartForBoxNum = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        const parentBox = cartForBoxNum.items.find(item => item.key === parentLineKey);
        const randomFillBoxNum = parentBox && parentBox.properties && parentBox.properties._box_number
            ? parentBox.properties._box_number : null;

        const items = chosenVariantIds.map((variantId) => {
            const item = {
                id: Number(variantId),
                quantity: 1,
                parent_line_key: parentLineKey,
                // Always set a unique _sample_id so identical samples aren't merged into one line.
                properties: {
                    _sample_id: Date.now() + "_" + Math.random(),
                },
            };
            // Mirror the parent box's number so the box function can group its samples.
            if (randomFillBoxNum) {
                item.properties._box_number = randomFillBoxNum;
            }
            return item;
        });

        const addRes = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({ items }),
        });

        if (!addRes.ok) {
            const err = await addRes.json().catch(() => ({}));
            console.error("[SampleBox] Error adding random samples", err);
            return;
        }

        // 4) Trigger cart refresh (adjust to your theme's behavior if needed)
        document.dispatchEvent(new CustomEvent("cart:refresh"));
    } catch (error) {
        console.error("[SampleBox] Random fill failed", error);
    } finally {
        btn.disabled = false;
        btn.removeAttribute("aria-busy");
    }
});

document.addEventListener('DOMContentLoaded', () => {
    const BOX_5X_CHECKOUT_ID = 64444973384057;
    const BOX_10X_CHECKOUT_ID = 64444973416825;

    function getParentKey(item) {
        if (!item.parent_relationship) return null;
        return item.parent_relationship.parent_key ||
            (item.parent_relationship.parent && item.parent_relationship.parent.key);
    }

    // Intercept all checkout button clicks
    document.addEventListener('click', async (event) => {
        const checkoutButton = event.target.closest('button[name="checkout"], a[href*="/checkout"]');
        if (!checkoutButton) return;

        // Prevent default checkout
        event.preventDefault();
        event.stopPropagation();

        try {
            // Get current cart
            const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

            // Find all parent boxes (both 5x and 10x)
            const parentBoxes = cart.items.filter(item =>
                item.id === BOX_5X_CHECKOUT_ID || item.id === BOX_10X_CHECKOUT_ID
            );

            if (parentBoxes.length === 0) {
                // No boxes, proceed to checkout
                proceedToCheckout();
                return;
            }

            // Check each box for completeness
            let hasIncompleteBox = false;
            let totalEmptySlots = 0;
            const incompleteBoxes = [];

            for (const parentBox of parentBoxes) {
                // Count children for this box
                const childrenCount = cart.items.filter(item =>
                    getParentKey(item) === parentBox.key
                ).length;

                // Determine box capacity from variant
                const boxCapacity = parentBox.id === BOX_10X_CHECKOUT_ID ? 10 : 5;

                const emptySlots = boxCapacity - childrenCount;

                if (emptySlots > 0) {
                    hasIncompleteBox = true;
                    totalEmptySlots += emptySlots;
                    incompleteBoxes.push({
                        key: parentBox.key,
                        childrenCount,
                        boxCapacity,
                        emptySlots
                    });
                }
            }

            if (hasIncompleteBox) {
                // Show incomplete box dialog
                showIncompleteBoxDialog(incompleteBoxes, totalEmptySlots);
            } else {
                // All boxes complete, proceed to checkout
                proceedToCheckout();
            }

        } catch (error) {
            console.error('Error checking boxes:', error);
            // On error, proceed to checkout anyway
            proceedToCheckout();
        }
    });

    async function proceedToCheckout() {
        try {
            // Fetch the cart from the domain root (no locale prefix) so item
            // titles come back in the default/primary language (German) rather
            // than the active storefront locale (e.g. English).
            const cart = await (await fetch('/cart.js')).json();

            const parentBoxes = cart.items.filter(item =>
                item.id === BOX_5X_CHECKOUT_ID || item.id === BOX_10X_CHECKOUT_ID
            );

            // Build the final order note from customer message + sample box info
            const customerNote = ((cart.attributes && cart.attributes.customer_note) || '').trim();
            let boxNote = '';

            if (parentBoxes.length > 0) {
                const noteLines = [];

                parentBoxes.forEach((box, index) => {
                    const boxLabel = box.id === BOX_5X_CHECKOUT_ID ? '5er Box' : '10er Box';
                    const boxNumber = parentBoxes.length > 1 ? ` ${index + 1}` : '';

                    const children = cart.items.filter(item =>
                        getParentKey(item) === box.key
                    );

                    noteLines.push(`── Sample Box${boxNumber} (${boxLabel}) ──`);
                    children.forEach((child, i) => {
                        // Strip " – Sample" suffix for cleaner names
                        const name = child.title.replace(/\s*–\s*Sample$/i, '');
                        noteLines.push(`  ${i + 1}. ${name}`);
                    });
                    noteLines.push('');
                });

                boxNote = noteLines.join('\n').trim();
            }

            const fullNote = [customerNote, boxNote].filter(Boolean).join('\n\n');

            await fetch(`${Shopify.routes.root}cart/update.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ note: fullNote })
            });
        } catch (error) {
            console.error('Error setting sample box order note:', error);
        }

        window.location.href = `${Shopify.routes.root}checkout`;
    }

    async function showIncompleteBoxDialog(incompleteBoxes, totalEmptySlots) {
        const dialog = document.getElementById('incomplete-box-dialog');
        const slotsCountElement = dialog.querySelector('.incomplete-slots-count');

        const strings = window.themeVariables.strings;
        slotsCountElement.textContent = totalEmptySlots === 1
            ? strings.sampleBoxIncompleteSlotsOne
            : strings.sampleBoxIncompleteSlotsOther.replace('@@', totalEmptySlots);

        // Store incomplete boxes data for later use
        dialog.setAttribute('data-incomplete-boxes', JSON.stringify(incompleteBoxes));

        // Show "Box löschen und Rest zur Kasse" only if there are non-box items in the cart
        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        const boxItemKeys = new Set();
        const parentBoxes = cart.items.filter(item =>
            item.id === BOX_5X_CHECKOUT_ID || item.id === BOX_10X_CHECKOUT_ID
        );
        parentBoxes.forEach(box => {
            boxItemKeys.add(box.key);
            cart.items.forEach(item => {
                if (getParentKey(item) === box.key) {
                    boxItemKeys.add(item.key);
                }
            });
        });
        const hasNonBoxItems = cart.items.some(item => !boxItemKeys.has(item.key));
        const checkoutAnywayBtn = dialog.querySelector('[data-action="checkout-anyway"]');
        checkoutAnywayBtn.style.display = hasNonBoxItems ? '' : 'none';

        dialog.show();
    }

    // Handle "Auto-fill box" action
    const dialog = document.getElementById('incomplete-box-dialog');

    if (!dialog) return;

    dialog.querySelector('[data-action="auto-fill"]').addEventListener('click', async function () {
        this.setAttribute('aria-busy', 'true');
        this.disabled = true;

        try {
            const incompleteBoxes = JSON.parse(dialog.getAttribute('data-incomplete-boxes'));

            // Get cart again to ensure fresh data
            const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

            // Get all existing sample IDs and brands
            const existingIds = [];
            const existingBrands = [];

            cart.items.forEach(item => {
                if (item.parent_relationship) {
                    existingIds.push(String(item.id));
                    existingBrands.push(item.vendor.toLowerCase());
                }
            });

            // Load samples collection
            const res = await fetch('/collections/samples/products.json?limit=250');
            if (!res.ok) {
                throw new Error('Failed to load samples');
            }

            const data = await res.json();
            const candidateVariants = [];

            // Collect all available variants with their brand info
            (data.products || []).forEach((product) => {
                const brand = (product.vendor || "").toLowerCase();

                (product.variants || []).forEach((variant) => {
                    if (!variant.available) return;
                    const vid = String(variant.id);
                    if (!existingIds.includes(vid)) {
                        candidateVariants.push({
                            id: vid,
                            brand: brand,
                        });
                    }
                });
            });

            // Fill each incomplete box
            for (const box of incompleteBoxes) {
                const usedBrands = new Set(existingBrands);
                const chosenVariantIds = [];
                const pool = [...candidateVariants];

                for (let i = 0; i < box.emptySlots && pool.length > 0; i++) {
                    // Filter pool to exclude brands already used
                    const availablePool = pool.filter(v => !usedBrands.has(v.brand));

                    if (availablePool.length === 0) {
                        console.log('[IncompleteBox] No more unique brands available');
                        break;
                    }

                    // Pick random variant from available pool
                    const idx = Math.floor(Math.random() * availablePool.length);
                    const chosen = availablePool[idx];

                    chosenVariantIds.push(chosen.id);
                    usedBrands.add(chosen.brand);
                    existingBrands.push(chosen.brand); // Add to global list too

                    // Remove the chosen variant from the main pool
                    const poolIdx = pool.findIndex(v => v.id === chosen.id);
                    if (poolIdx > -1) {
                        pool.splice(poolIdx, 1);
                    }
                }

                if (chosenVariantIds.length > 0) {
                    // Mirror the box's number onto its samples - read from box's own properties (5x or 10x)
                    const boxItem = cart.items.find(item => item.key === box.key);
                    const autoFillBoxNum = boxItem && boxItem.properties && boxItem.properties._box_number
                        ? boxItem.properties._box_number : null;

                    // Add samples to this box
                    const items = chosenVariantIds.map((variantId) => {
                        const props = { _sample_id: Date.now() + "_" + Math.random() };
                        if (autoFillBoxNum) {
                            props._box_number = autoFillBoxNum;
                        }
                        return {
                            id: Number(variantId),
                            quantity: 1,
                            parent_line_key: box.key,
                            properties: props
                        };
                    });

                    await fetch("/cart/add.js", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest",
                        },
                        body: JSON.stringify({ items }),
                    });
                }
            }

            // Refresh cart
            document.dispatchEvent(new CustomEvent("cart:refresh"));

            // Close dialog and proceed to checkout
            dialog.hide();

            // Small delay to ensure cart is updated
            setTimeout(() => {
                proceedToCheckout();
            }, 500);

        } catch (error) {
            console.error('Error auto-filling boxes:', error);
            alert('Es ist ein Fehler aufgetreten. Bitte versuche es erneut.');
        } finally {
            this.removeAttribute('aria-busy');
            this.disabled = false;
        }
    });

    // Handle "Box löschen und Rest zur Kasse" action
    dialog.querySelector('[data-action="checkout-anyway"]').addEventListener('click', async function () {
        this.setAttribute('aria-busy', 'true');
        this.disabled = true;

        try {
            // Get current cart and remove all box items (parents + children)
            const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
            const keysToRemove = {};

            const parentBoxes = cart.items.filter(item =>
                item.id === BOX_5X_CHECKOUT_ID || item.id === BOX_10X_CHECKOUT_ID
            );

            parentBoxes.forEach(box => {
                keysToRemove[box.key] = 0;
                cart.items.forEach(item => {
                    if (getParentKey(item) === box.key) {
                        keysToRemove[item.key] = 0;
                    }
                });
            });

            await fetch(`${Shopify.routes.root}cart/update.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: keysToRemove })
            });

            document.dispatchEvent(new CustomEvent("cart:refresh"));

            dialog.hide();
            setTimeout(() => {
                proceedToCheckout();
            }, 500);
        } catch (error) {
            console.error('Error removing box items:', error);
            alert('Es ist ein Fehler aufgetreten. Bitte versuche es erneut.');
        } finally {
            this.removeAttribute('aria-busy');
            this.disabled = false;
        }
    });
});

document.addEventListener('DOMContentLoaded', () => {
    const newBoxDialog = document.getElementById('new-box-dialog');
    const boxFullDialog = document.getElementById('box-full-dialog');
    const duplicateDialog = document.getElementById('duplicate-sample-dialog');
    const BOX_5X_VARIANT_ID = 64444973384057;
    const BOX_10X_VARIANT_ID = 64444973416825;

    if (!newBoxDialog) return;

    // Get the box number from a box's own properties (5x or 10x)
    function getBoxNumber(cart, boxKey) {
        const box = cart.items.find(item => item.key === boxKey);
        if (box && box.properties && box.properties._box_number) {
            return box.properties._box_number;
        }
        return null;
    }

    // Get the next sequential box number across all boxes (5x and 10x)
    function getNextBoxNumber(cart) {
        const boxes = cart.items.filter(item =>
            item.id === BOX_5X_VARIANT_ID || item.id === BOX_10X_VARIANT_ID
        );
        let maxNum = 0;
        boxes.forEach(box => {
            const num = parseInt(box.properties && box.properties._box_number || '0', 10);
            if (num > maxNum) maxNum = num;
        });
        return maxNum + 1;
    }

    // Check if a sample variant is already in any box
    function isSampleInAnyBox(cart, sampleVariantId) {
        const boxes = cart.items.filter(item =>
            item.id === BOX_5X_VARIANT_ID || item.id === BOX_10X_VARIANT_ID
        );
        for (const box of boxes) {
            const children = cart.items.filter(item => {
                if (!item.parent_relationship) return false;
                const parentKey = item.parent_relationship.parent_key ||
                    (item.parent_relationship.parent && item.parent_relationship.parent.key);
                return parentKey === box.key;
            });
            if (children.some(child => child.id === parseInt(sampleVariantId))) {
                return true;
            }
        }
        return false;
    }

    // Show duplicate dialog
    function showDuplicateDialog(sampleVariantId) {
        if (!duplicateDialog) return;
        duplicateDialog.querySelectorAll('[data-sample-variant-id]').forEach(btn => {
            btn.setAttribute('data-sample-variant-id', sampleVariantId);
        });
        duplicateDialog.show();
    }

    // Show dialog when clicking the add sample button
    document.addEventListener('click', async (event) => {
        const button = event.target.closest('.show-new-box-dialog');
        if (!button) return;

        event.preventDefault();

        // Show loading state immediately
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;

        const sampleVariantId = button.getAttribute('data-sample-variant-id');

        try {
            // Check if there are any boxes in the cart
            const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
            const existingBoxes = cart.items.filter(item =>
                item.id === BOX_5X_VARIANT_ID || item.id === BOX_10X_VARIANT_ID
            );

            // Check for duplicate sample in any box
            if (isSampleInAnyBox(cart, sampleVariantId)) {
                button.removeAttribute('aria-busy');
                button.disabled = false;
                showDuplicateDialog(sampleVariantId);
                return;
            }

            if (existingBoxes.length > 0) {
                // Boxes exist - find one with space and add directly
                let targetBox = null;
                let full5xBox = null; // Track if we have a full 5x box

                for (const box of existingBoxes) {
                    // Count children
                    const childrenCount = cart.items.filter(item => {
                        if (!item.parent_relationship) return false;
                        const parentKey = item.parent_relationship.parent_key ||
                            (item.parent_relationship.parent && item.parent_relationship.parent.key);
                        return parentKey === box.key;
                    }).length;

                    const boxCapacity = box.id === BOX_5X_VARIANT_ID ? 5 : 10;

                    if (childrenCount < boxCapacity) {
                        targetBox = box;
                        break;
                    } else if (box.id === BOX_5X_VARIANT_ID && childrenCount === 5) {
                        // Found a full 5x box that could be extended
                        full5xBox = box;
                    }
                }

                if (targetBox) {
                    // Add to existing box with space
                    console.log('Adding to existing box:', targetBox.key);

                    // Mirror the box's number onto the sample (both 5x and 10x boxes have one)
                    const targetBoxNum = getBoxNumber(cart, targetBox.key);
                    const sampleProps = { _sample_id: Date.now() + "_" + Math.random() };
                    if (targetBoxNum) {
                        sampleProps._box_number = String(targetBoxNum);
                    }

                    try {
                        const addResponse = await fetch('/cart/add.js', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-Requested-With': 'XMLHttpRequest',
                            },
                            body: JSON.stringify({
                                items: [{
                                    id: parseInt(sampleVariantId),
                                    quantity: 1,
                                    parent_line_key: targetBox.key,
                                    properties: sampleProps
                                }]
                            }),
                        });

                        const addResult = await addResponse.json();

                        if (!addResponse.ok) {
                            throw new Error(addResult.description || 'Failed to add to cart');
                        }

                        // Force refresh the cart drawer
                        document.dispatchEvent(new CustomEvent('cart:refresh'));

                        // Wait a bit for refresh, then open cart drawer
                        setTimeout(() => {
                            if (window.themeVariables.settings.cartType === 'drawer') {
                                const cartDrawer = document.getElementById('cart-drawer');
                                if (cartDrawer) {
                                    cartDrawer.show();
                                }
                            }
                        }, 500);

                    } catch (error) {
                        console.error('Error adding sample:', error);
                        alert('Es ist ein Fehler aufgetreten: ' + error.message);
                    } finally {
                        button.removeAttribute('aria-busy');
                        button.disabled = false;
                    }

                    return; // Exit - don't show dialog
                } else {
                    // All boxes are full - show box-full dialog
                    button.removeAttribute('aria-busy');
                    button.disabled = false;

                    // Set sample variant ID on all buttons
                    boxFullDialog.querySelectorAll('[data-sample-variant-id]').forEach(btn => {
                        btn.setAttribute('data-sample-variant-id', sampleVariantId);
                    });

                    // Show/hide extend button based on whether we have a full 5x box
                    const extendButton = boxFullDialog.querySelector('[data-action="extend-to-10x"]');
                    if (full5xBox) {
                        extendButton.style.display = 'block';
                        extendButton.setAttribute('data-box-key', full5xBox.key);
                        boxFullDialog.querySelector('.box-full-message').textContent =
                            window.themeVariables.strings.sampleBoxFullMessage5x;
                    } else {
                        extendButton.style.display = 'none';
                        boxFullDialog.querySelector('.box-full-message').textContent =
                            window.themeVariables.strings.sampleBoxFullMessageDefault;
                    }

                    boxFullDialog.show();
                    return;
                }
            }

            // No boxes - show new-box dialog
            button.removeAttribute('aria-busy');
            button.disabled = false;

            newBoxDialog.querySelectorAll('[data-sample-variant-id]').forEach(btn => {
                btn.setAttribute('data-sample-variant-id', sampleVariantId);
            });
            newBoxDialog.show();

        } catch (error) {
            console.error('Error checking cart:', error);

            // Remove loading state
            button.removeAttribute('aria-busy');
            button.disabled = false;

            // On error, show new-box dialog as fallback
            newBoxDialog.querySelectorAll('[data-sample-variant-id]').forEach(btn => {
                btn.setAttribute('data-sample-variant-id', sampleVariantId);
            });
            newBoxDialog.show();
        }
    });

    // Handle adding sample to a box (5x or 10x)
    newBoxDialog.querySelectorAll('.add-sample-to-box').forEach(button => {
        button.addEventListener('click', async function () {
            const boxVariantId = parseInt(this.getAttribute('data-box-variant-id'));
            const sampleVariantId = parseInt(this.getAttribute('data-sample-variant-id'));

            console.log('Adding sample to box:', { boxVariantId, sampleVariantId });

            this.setAttribute('aria-busy', 'true');
            this.disabled = true;

            try {
                // Get current cart
                const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
                console.log('Current cart:', cart);

                // Find if this box type already exists WITH SPACE
                let existingBox = null;

                // Find all boxes of this type
                const boxesOfType = cart.items.filter(item => item.id === boxVariantId);

                // Check each box for available space
                for (const box of boxesOfType) {
                    const childrenCount = cart.items.filter(item => {
                        if (!item.parent_relationship) return false;
                        const parentKey = item.parent_relationship.parent_key ||
                            (item.parent_relationship.parent && item.parent_relationship.parent.key);
                        return parentKey === box.key;
                    }).length;

                    const boxCapacity = boxVariantId === BOX_5X_VARIANT_ID ? 5 : 10;

                    console.log(`Box ${box.key}: ${childrenCount}/${boxCapacity} full`);

                    if (childrenCount < boxCapacity) {
                        existingBox = box;
                        break;
                    }
                }

                console.log('Existing box with space:', existingBox);

                let addResponse;

                if (existingBox) {
                    // Box with space exists - add sample to it using parent_line_key
                    console.log('Adding to existing box with key:', existingBox.key);

                    const dialogSampleProps = { _sample_id: Date.now() + "_" + Math.random() };
                    const dialogBoxNum = getBoxNumber(cart, existingBox.key);
                    if (dialogBoxNum) dialogSampleProps._box_number = String(dialogBoxNum);

                    addResponse = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest',
                        },
                        body: JSON.stringify({
                            items: [{
                                id: sampleVariantId,
                                quantity: 1,
                                parent_line_key: existingBox.key,
                                properties: dialogSampleProps
                            }]
                        }),
                    });
                } else {
                    // No box with space - create new box and add sample
                    console.log('Creating new box with unique ID and adding sample');

                    const newBoxSampleProps = { _sample_id: Date.now() + "_" + Math.random() };
                    const newBoxProps = { _box_id: Date.now() + "_" + Math.random(), _new_sample_box: 'true' };
                    const nextNum = String(getNextBoxNumber(cart));
                    newBoxProps._box_number = nextNum;
                    newBoxSampleProps._box_number = nextNum;

                    addResponse = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest',
                        },
                        body: JSON.stringify({
                            items: [
                                {
                                    id: boxVariantId,
                                    quantity: 1,
                                    properties: newBoxProps
                                },
                                {
                                    id: sampleVariantId,
                                    quantity: 1,
                                    parent_id: boxVariantId,
                                    properties: newBoxSampleProps
                                }
                            ]
                        }),
                    });
                }

                const addResult = await addResponse.json();
                console.log('Add result:', addResult);

                if (!addResponse.ok) {
                    throw new Error(addResult.description || 'Failed to add to cart');
                }

                // Force refresh the cart drawer
                document.dispatchEvent(new CustomEvent('cart:refresh'));

                // Close dialog
                newBoxDialog.hide();

                // Wait for refresh, then open cart drawer
                setTimeout(() => {
                    if (window.themeVariables.settings.cartType === 'drawer') {
                        const cartDrawer = document.getElementById('cart-drawer');
                        if (cartDrawer) {
                            cartDrawer.show();
                        }
                    }
                }, 500);

            } catch (error) {
                console.error('Error adding sample to box:', error);
                alert('Es ist ein Fehler aufgetreten: ' + error.message);
            } finally {
                this.removeAttribute('aria-busy');
                this.disabled = false;
            }
        });
    });

    // Handle adding sample individually
    const individualButton = newBoxDialog.querySelector('.add-sample-individual');
    if (individualButton) {
        individualButton.addEventListener('click', async function () {
            const sampleVariantId = parseInt(this.getAttribute('data-sample-variant-id'));

            console.log('Adding individual sample:', sampleVariantId);

            this.setAttribute('aria-busy', 'true');
            this.disabled = true;

            try {
                const addResponse = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({
                        items: [{
                            id: sampleVariantId,
                            quantity: 1,
                            properties: {
                                _sample_id: Date.now() + "_" + Math.random()
                            }
                        }]
                    }),
                });

                const addResult = await addResponse.json();
                console.log('Add result:', addResult);

                if (!addResponse.ok) {
                    throw new Error(addResult.description || 'Failed to add to cart');
                }

                // Force refresh the cart drawer
                document.dispatchEvent(new CustomEvent('cart:refresh'));

                // Close dialog
                newBoxDialog.hide();

                // Wait for refresh, then open cart drawer
                setTimeout(() => {
                    if (window.themeVariables.settings.cartType === 'drawer') {
                        const cartDrawer = document.getElementById('cart-drawer');
                        if (cartDrawer) {
                            cartDrawer.show();
                        }
                    }
                }, 500);

            } catch (error) {
                console.error('Error adding individual sample:', error);
                alert('Es ist ein Fehler aufgetreten: ' + error.message);
            } finally {
                this.removeAttribute('aria-busy');
                this.disabled = false;
            }
        });
    }

    // =========================================================
    // Box-full dialog button handlers
    // =========================================================
    if (boxFullDialog) {
        // "Auf 10er-Box erweitern" button
        const extendButton = boxFullDialog.querySelector('[data-action="extend-to-10x"]');
        if (extendButton) {
            extendButton.addEventListener('click', async function () {
                this.setAttribute('aria-busy', 'true');
                this.disabled = true;

                try {
                    const sampleVariantId = parseInt(this.getAttribute('data-sample-variant-id'));
                    const oldBoxKey = this.getAttribute('data-box-key');

                    // Get current cart
                    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

                    // Find all children of the old 5x box
                    const childItems = cart.items.filter(item => {
                        if (!item.parent_relationship) return false;
                        const parentKey = item.parent_relationship.parent_key ||
                            (item.parent_relationship.parent && item.parent_relationship.parent.key);
                        return parentKey === oldBoxKey;
                    });

                    // Remove old 5x box and all its children
                    const updates = {};
                    updates[oldBoxKey] = 0;
                    childItems.forEach(child => { updates[child.key] = 0; });

                    await fetch(`${Shopify.routes.root}cart/update.js`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                        body: JSON.stringify({ updates })
                    });

                    // Create new 10x box with all existing children + the new sample
                    // Get next box number for this new 10x box
                    const extendCartRefresh = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
                    const extendBoxNum = getNextBoxNumber(extendCartRefresh);

                    const extendBoxNumStr = String(extendBoxNum);
                    const items = [{
                        id: BOX_10X_VARIANT_ID,
                        quantity: 1,
                        properties: {
                            _box_id: Date.now() + "_" + Math.random(),
                            _box_number: extendBoxNumStr,
                            _new_sample_box: 'true'
                        }
                    }];

                    // Re-add existing children with _box_number
                    childItems.forEach(child => {
                        items.push({
                            id: child.id,
                            quantity: 1,
                            parent_id: BOX_10X_VARIANT_ID,
                            properties: {
                                _sample_id: Date.now() + "_" + Math.random(),
                                _box_number: String(extendBoxNum)
                            }
                        });
                    });

                    // Add the new sample with _box_number
                    items.push({
                        id: sampleVariantId,
                        quantity: 1,
                        parent_id: BOX_10X_VARIANT_ID,
                        properties: {
                            _sample_id: Date.now() + "_" + Math.random(),
                            _box_number: String(extendBoxNum)
                        }
                    });

                    await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                        body: JSON.stringify({ items })
                    });

                    document.dispatchEvent(new CustomEvent('cart:refresh'));
                    boxFullDialog.hide();

                    setTimeout(() => {
                        if (window.themeVariables.settings.cartType === 'drawer') {
                            const cartDrawer = document.getElementById('cart-drawer');
                            if (cartDrawer) cartDrawer.show();
                        }
                    }, 500);

                } catch (error) {
                    console.error('Error extending to 10x box:', error);
                    alert('Es ist ein Fehler aufgetreten: ' + error.message);
                } finally {
                    this.removeAttribute('aria-busy');
                    this.disabled = false;
                }
            });
        }

        // "Neue 5er-Box erstellen" button
        const add5xButton = boxFullDialog.querySelector('[data-action="add-5x-box"]');
        if (add5xButton) {
            add5xButton.addEventListener('click', async function () {
                this.setAttribute('aria-busy', 'true');
                this.disabled = true;

                try {
                    const sampleVariantId = parseInt(this.getAttribute('data-sample-variant-id'));

                    // Assign a sequential box number so the box function can group this box's samples.
                    const add5xCart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
                    const add5xBoxNum = String(getNextBoxNumber(add5xCart));

                    const addResponse = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                        body: JSON.stringify({
                            items: [
                                {
                                    id: BOX_5X_VARIANT_ID,
                                    quantity: 1,
                                    properties: { _box_id: Date.now() + "_" + Math.random(), _box_number: add5xBoxNum, _new_sample_box: 'true' }
                                },
                                {
                                    id: sampleVariantId,
                                    quantity: 1,
                                    parent_id: BOX_5X_VARIANT_ID,
                                    properties: { _sample_id: Date.now() + "_" + Math.random(), _box_number: add5xBoxNum }
                                }
                            ]
                        })
                    });

                    if (!addResponse.ok) {
                        const err = await addResponse.json().catch(() => ({}));
                        throw new Error(err.description || 'Failed to create box');
                    }

                    document.dispatchEvent(new CustomEvent('cart:refresh'));
                    boxFullDialog.hide();

                    setTimeout(() => {
                        if (window.themeVariables.settings.cartType === 'drawer') {
                            const cartDrawer = document.getElementById('cart-drawer');
                            if (cartDrawer) cartDrawer.show();
                        }
                    }, 500);

                } catch (error) {
                    console.error('Error creating new 5x box:', error);
                    alert('Es ist ein Fehler aufgetreten: ' + error.message);
                } finally {
                    this.removeAttribute('aria-busy');
                    this.disabled = false;
                }
            });
        }

        // "Probe einzeln hinzufügen" button
        const boxFullIndividualButton = boxFullDialog.querySelector('.add-sample-individual');
        if (boxFullIndividualButton) {
            boxFullIndividualButton.addEventListener('click', async function () {
                this.setAttribute('aria-busy', 'true');
                this.disabled = true;

                try {
                    const sampleVariantId = parseInt(this.getAttribute('data-sample-variant-id'));

                    const addResponse = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                        body: JSON.stringify({
                            items: [{
                                id: sampleVariantId,
                                quantity: 1,
                                properties: { _sample_id: Date.now() + "_" + Math.random() }
                            }]
                        })
                    });

                    if (!addResponse.ok) {
                        const err = await addResponse.json().catch(() => ({}));
                        throw new Error(err.description || 'Failed to add sample');
                    }

                    document.dispatchEvent(new CustomEvent('cart:refresh'));
                    boxFullDialog.hide();

                    setTimeout(() => {
                        if (window.themeVariables.settings.cartType === 'drawer') {
                            const cartDrawer = document.getElementById('cart-drawer');
                            if (cartDrawer) cartDrawer.show();
                        }
                    }, 500);

                } catch (error) {
                    console.error('Error adding individual sample:', error);
                    alert('Es ist ein Fehler aufgetreten: ' + error.message);
                } finally {
                    this.removeAttribute('aria-busy');
                    this.disabled = false;
                }
            });
        }
    }

    // =========================================================
    // Duplicate-sample dialog button handlers
    // =========================================================
    if (duplicateDialog) {
        // "Weiter suchen" button - just close the dialog
        const continueBtn = duplicateDialog.querySelector('.duplicate-continue-shopping');
        if (continueBtn) {
            continueBtn.addEventListener('click', function () {
                duplicateDialog.hide();
            });
        }

        // "Probe einzeln kaufen" button
        const buyIndividualBtn = duplicateDialog.querySelector('.duplicate-buy-individual');
        if (buyIndividualBtn) {
            buyIndividualBtn.addEventListener('click', async function () {
                this.setAttribute('aria-busy', 'true');
                this.disabled = true;

                try {
                    const sampleVariantId = parseInt(this.getAttribute('data-sample-variant-id'));

                    const addResponse = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                        body: JSON.stringify({
                            items: [{
                                id: sampleVariantId,
                                quantity: 1,
                                properties: { _sample_id: Date.now() + "_" + Math.random() }
                            }]
                        })
                    });

                    if (!addResponse.ok) {
                        const err = await addResponse.json().catch(() => ({}));
                        throw new Error(err.description || 'Failed to add sample');
                    }

                    document.dispatchEvent(new CustomEvent('cart:refresh'));
                    duplicateDialog.hide();

                    setTimeout(() => {
                        if (window.themeVariables.settings.cartType === 'drawer') {
                            const cartDrawer = document.getElementById('cart-drawer');
                            if (cartDrawer) cartDrawer.show();
                        }
                    }, 500);

                } catch (error) {
                    console.error('Error adding individual sample:', error);
                    alert('Es ist ein Fehler aufgetreten: ' + error.message);
                } finally {
                    this.removeAttribute('aria-busy');
                    this.disabled = false;
                }
            });
        }
    }
});

document.addEventListener('click', async (event) => {
    const deleteButton = event.target.closest('.sample-box-delete-button');
    if (!deleteButton) return;

    event.preventDefault();

    const boxKey = deleteButton.getAttribute('data-box-key');
    const removeUrl = deleteButton.getAttribute('data-box-url-remove');

    // Show loading state
    deleteButton.setAttribute('aria-busy', 'true');
    deleteButton.disabled = true;

    try {
        // Get current cart to find all children
        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

        // Find all children of this box
        const childItems = cart.items.filter(item => {
            if (!item.parent_relationship) return false;
            const parentKey = item.parent_relationship.parent_key ||
                (item.parent_relationship.parent && item.parent_relationship.parent.key);
            return parentKey === boxKey;
        });

        console.log('Removing box and', childItems.length, 'children');

        // Build updates object to remove parent and all children
        const updates = {};

        // Remove all children (set quantity to 0)
        childItems.forEach(child => {
            updates[child.key] = 0;
        });

        // Remove the parent box (set quantity to 0)
        updates[boxKey] = 0;

        // Update cart
        const updateResponse = await fetch(`${Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ updates })
        });

        if (!updateResponse.ok) {
            throw new Error('Failed to remove box');
        }

        // Refresh the cart drawer
        document.dispatchEvent(new CustomEvent('cart:refresh'));

    } catch (error) {
        console.error('Error removing box:', error);
        alert('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
        deleteButton.removeAttribute('aria-busy');
        deleteButton.disabled = false;
    }
});